import {useCallback, useMemo} from 'react'
import {useSearchParams} from 'react-router'

import {
  ChartViewMode,
  CHART_VIEW_MODES,
  DifficultyFilter,
  DIFFICULTY_FILTER_ALL,
  DIFFICULTY_FILTER_OPTIONS,
  ValueDisplayMode,
  VALUE_DISPLAY_MODES
} from '../../types'

const PARAM_VIEW = 'view'
const PARAM_DIFFICULTY = 'difficulty'
const PARAM_CATEGORIES = 'categories'
const PARAM_DISPLAY = 'display'
const PARAM_SERIES = 'series'
const PARAM_YEAR = 'year'

const CURRENT_YEAR = new Date().getFullYear()

const DEFAULT_VIEW: ChartViewMode = 'monthly'
const DEFAULT_DIFFICULTY: DifficultyFilter = DIFFICULTY_FILTER_ALL
const DEFAULT_DISPLAY: ValueDisplayMode = 'total'
const DEFAULT_YEAR = CURRENT_YEAR - 1

export const useChartQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const viewMode = useMemo<ChartViewMode>(() => {
    const raw = searchParams.get(PARAM_VIEW)
    return raw && (CHART_VIEW_MODES as readonly string[]).includes(raw)
      ? (raw as ChartViewMode)
      : DEFAULT_VIEW
  }, [searchParams])

  const difficultyFilter = useMemo<DifficultyFilter>(() => {
    const raw = searchParams.get(PARAM_DIFFICULTY)
    return raw && (DIFFICULTY_FILTER_OPTIONS as readonly string[]).includes(raw)
      ? (raw as DifficultyFilter)
      : DEFAULT_DIFFICULTY
  }, [searchParams])

  const categoryFilter = useMemo<string[]>(() => {
    const raw = searchParams.get(PARAM_CATEGORIES)
    if (!raw) return []
    return raw.split(',').filter(Boolean)
  }, [searchParams])

  const valueDisplayMode = useMemo<ValueDisplayMode>(() => {
    const raw = searchParams.get(PARAM_DISPLAY)
    return raw && (VALUE_DISPLAY_MODES as readonly string[]).includes(raw)
      ? (raw as ValueDisplayMode)
      : DEFAULT_DISPLAY
  }, [searchParams])

  const visibleSeriesIds = useMemo<string[] | null>(() => {
    const raw = searchParams.get(PARAM_SERIES)
    if (!raw) return null
    const ids = raw.split(',').filter(Boolean)
    return ids.length > 0 ? ids : null
  }, [searchParams])

  const selectedYear = useMemo<number>(() => {
    const raw = searchParams.get(PARAM_YEAR)
    const parsed = raw ? parseInt(raw, 10) : NaN
    return !isNaN(parsed) && parsed >= 2020 && parsed <= CURRENT_YEAR ? parsed : DEFAULT_YEAR
  }, [searchParams])

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null) {
            next.delete(key)
          } else {
            next.set(key, value)
          }
          return next
        },
        {replace: true}
      )
    },
    [setSearchParams]
  )

  const setViewMode = useCallback(
    (mode: ChartViewMode) => {
      updateParam(PARAM_VIEW, mode === DEFAULT_VIEW ? null : mode)
    },
    [updateParam]
  )

  const setDifficultyFilter = useCallback(
    (filter: DifficultyFilter) => {
      updateParam(PARAM_DIFFICULTY, filter === DEFAULT_DIFFICULTY ? null : filter)
    },
    [updateParam]
  )

  const setCategoryFilter = useCallback(
    (sectionIds: string[]) => {
      updateParam(PARAM_CATEGORIES, sectionIds.length > 0 ? sectionIds.join(',') : null)
    },
    [updateParam]
  )

  const setValueDisplayMode = useCallback(
    (mode: ValueDisplayMode) => {
      updateParam(PARAM_DISPLAY, mode === DEFAULT_DISPLAY ? null : mode)
    },
    [updateParam]
  )

  const setVisibleSeriesIds = useCallback(
    (ids: string[] | null) => {
      updateParam(PARAM_SERIES, ids && ids.length > 0 ? ids.join(',') : null)
    },
    [updateParam]
  )

  const setSelectedYear = useCallback(
    (year: number) => {
      updateParam(PARAM_YEAR, year === DEFAULT_YEAR ? null : String(year))
    },
    [updateParam]
  )

  return {
    viewMode,
    setViewMode,
    difficultyFilter,
    setDifficultyFilter,
    categoryFilter,
    setCategoryFilter,
    valueDisplayMode,
    setValueDisplayMode,
    visibleSeriesIds,
    setVisibleSeriesIds,
    selectedYear,
    setSelectedYear
  }
}
